import { useEffect, useMemo } from 'react';

import {
  Box,
  Button,
  Flex,
  Text,
  useToast,
  Link,
  useDisclosure,
  VStack,
  Spinner,
  Tooltip,
} from '@chakra-ui/react';

import { useParams } from 'react-router-dom';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useAppSelector, useAppDispatch } from 'hooks';
import { useFeatureFlag } from 'hooks/useFeatureFlag';

import {
  apiCreateESigning,
  apiGetESigning,
  apiResetSigning,
  apiUploadWetSignedPDF,
  apiCancelESigning,
  apiSendESigningReminder,
} from 'api';

import { setSigning as setAutoSigning } from 'reducers/autoPersonalFormReducer';
import { setSigning as setHomeSigning } from 'reducers/homePersonalFormReducer';
import { setSigning as setCommercialSigning } from 'reducers/commercialFormReducer';

import type { APISigning } from 'types/api.types';
import type { RootState } from 'store';

import {
  SIGNING_STATUS_PENDING,
  SIGNING_STATUS_COMPLETED,
  SIGNING_STATUS_ERROR,
  SIGNING_STATUS_CANCELLED,
  BINDER_STATUS_COMPLETED,
  STATUS_PENDING_CANCELLATION,
} from 'variables/constants';

import StepContainer from './StepContainer';
import StepHeader from './StepHeader';
import StatusBadge from './StatusBadge';
import SigningLogModal from './modals/SigningLogModal';
import WetSignUploadModal from './modals/WetSignUploadModal';
import CancelSigningModal from './modals/CancelSigningModal';
import ConfirmSigningModal from './modals/ConfirmSigningModal';
import type { Signer } from './modals/ConfirmSigningModal';
import type { BinderFormStateKey } from './stepTypes';
import { getSigningEmails } from './stepSelectors';

interface StepTwoProps {
  formStateKey: BinderFormStateKey;
}

const ACTIONS = {
  autoPersonalForm: setAutoSigning,
  homePersonalForm: setHomeSigning,
  commercialForm: setCommercialSigning,
};

const StepTwo = ({ formStateKey }: StepTwoProps) => {
  const { uuid } = useParams();
  const dispatch = useAppDispatch();
  const toast = useToast();
  const queryClient = useQueryClient();
  const { isOpen, onOpen, onClose } = useDisclosure();

  const {
    isOpen: isConfirmModalOpen,
    onOpen: onConfirmModalOpen,
    onClose: onConfirmModalClose,
  } = useDisclosure();

  const {
    isOpen: isWetSignModalOpen,
    onOpen: onWetSignModalOpen,
    onClose: onWetSignModalClose,
  } = useDisclosure();

  const {
    isOpen: isCancelModalOpen,
    onOpen: onCancelModalOpen,
    onClose: onCancelModalClose,
  } = useDisclosure();

  const setSigning = ACTIONS[formStateKey];

  const isSigningReminderEnabled = useFeatureFlag('enable-e-signing-reminder');

  const binderPdf = useAppSelector(
    (state: RootState) => state[formStateKey].binder_pdf,
  );
  const signing = useAppSelector(
    (state: RootState) => state[formStateKey].signing,
  );

  const emailData = useAppSelector((state: RootState) =>
    getSigningEmails(state, formStateKey),
  );

  const {
    brokerEmail,
    applicantEmail,
    coApplicantEmail,
    accountHolderSameAsApplicant,
    accountHolderSameAsCoApplicant,
    accountHolderEmail,
    secondaryAccountHolder,
    secondAccountHolderSameAsCoApplicant,
    showAccountHolderEmails,
    retireeEmails,
    excludedDriverEmails,
    additionalInsureds,
    commercialCoApplicants,
    supervisor,
  } = emailData;

  const signers = useMemo(() => {
    const result: Signer[] = [];

    if (brokerEmail) {
      result.push({ role: 'broker', email: brokerEmail });
    }
    if (supervisor?.email) {
      result.push({
        role: 'supervisor',
        email: supervisor.email,
        name: supervisor.name,
      });
    }
    if (applicantEmail) {
      result.push({ role: 'applicant', email: applicantEmail });
    }
    retireeEmails.forEach((retireeEmail) => {
      result.push({
        role: 'applicant',
        email: retireeEmail,
        name: 'Retiree Driver',
      });
    });
    excludedDriverEmails.forEach((excludedDriverEmail) => {
      result.push({
        role: 'excluded_driver',
        email: excludedDriverEmail,
        name: 'Excluded Driver',
      });
    });
    if (coApplicantEmail) {
      result.push({ role: 'co_applicant', email: coApplicantEmail });
    }
    commercialCoApplicants.forEach((entry) => {
      const email = entry.email?.trim();
      if (!email) return;
      result.push({
        role: 'co_applicant',
        email,
        name: entry.name?.trim() || undefined,
      });
    });
    if (
      showAccountHolderEmails &&
      !accountHolderSameAsApplicant &&
      !accountHolderSameAsCoApplicant &&
      accountHolderEmail
    ) {
      result.push({ role: 'account_holder', email: accountHolderEmail });
    }
    if (
      showAccountHolderEmails &&
      !secondAccountHolderSameAsCoApplicant &&
      secondaryAccountHolder
    ) {
      result.push({
        role: 'secondary_account_holder',
        email: secondaryAccountHolder,
      });
    }

    additionalInsureds.forEach((insured, index) => {
      if (insured.email?.trim()) {
        result.push({
          role: `additional_insured_${index + 1}`,
          email: insured.email.trim(),
        });
      }
    });

    return result;
  }, [
    brokerEmail,
    supervisor,
    applicantEmail,
    retireeEmails,
    excludedDriverEmails,
    coApplicantEmail,
    showAccountHolderEmails,
    accountHolderSameAsApplicant,
    accountHolderSameAsCoApplicant,
    accountHolderEmail,
    secondAccountHolderSameAsCoApplicant,
    secondaryAccountHolder,
    additionalInsureds,
    commercialCoApplicants,
  ]);

  const hasEmailConflict = () => {
    const emails = new Set();

    if (applicantEmail && coApplicantEmail) {
      const trimmedApplicantEmail = applicantEmail.toLowerCase().trim();
      const trimmedCoApplicantEmail = coApplicantEmail.toLowerCase().trim();
      if (trimmedApplicantEmail === trimmedCoApplicantEmail) {
        return true;
      }
    }

    if (
      showAccountHolderEmails &&
      !accountHolderSameAsApplicant &&
      !secondAccountHolderSameAsCoApplicant &&
      accountHolderEmail &&
      secondaryAccountHolder
    ) {
      const firstHolderEmail = accountHolderEmail.toLowerCase().trim();
      const secondHolderEmail = secondaryAccountHolder.toLowerCase().trim();
      if (firstHolderEmail === secondHolderEmail) {
        return true;
      }
    }

    if (applicantEmail) {
      emails.add(applicantEmail.toLowerCase().trim());
    }
    if (coApplicantEmail) {
      emails.add(coApplicantEmail.toLowerCase().trim());
    }

    if (
      showAccountHolderEmails &&
      !accountHolderSameAsApplicant &&
      !accountHolderSameAsCoApplicant &&
      accountHolderEmail
    ) {
      const holderEmail = accountHolderEmail.toLowerCase().trim();
      if (emails.has(holderEmail)) {
        return true;
      }
    }

    if (
      showAccountHolderEmails &&
      !secondAccountHolderSameAsCoApplicant &&
      secondaryAccountHolder
    ) {
      const secondaryEmail = secondaryAccountHolder.toLowerCase().trim();
      if (emails.has(secondaryEmail)) {
        return true;
      }
    }

    return false;
  };

  const createSigningMutation = useMutation({
    mutationFn: () => {
      if (!binderPdf?.id) {
        throw new Error('Binder ID is required for signing');
      }
      return apiCreateESigning(uuid, binderPdf.id);
    },
    onSuccess: (signingData: APISigning) => {
      dispatch(setSigning(signingData));
      queryClient.invalidateQueries({ queryKey: ['submission'] });

      toast({
        title: 'Signing initiated successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
        position: 'top',
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Error initiating signing',
        description: error?.error || 'Please try again later.',
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'top',
      });
    },
  });

  const resetSigningMutation = useMutation({
    mutationFn: () => {
      return apiResetSigning(uuid);
    },
    onSuccess: () => {
      dispatch(setSigning(null));
      queryClient.invalidateQueries({ queryKey: ['submission'] });

      toast({
        title: 'Signing reset successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
        position: 'top',
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Error resetting signing',
        description: error?.error || 'Please try again later.',
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'top',
      });
    },
  });

  const uploadWetSignedMutation = useMutation({
    mutationFn: (file: File) => {
      if (!binderPdf?.id) {
        throw new Error(
          'Binder ID is required for uploading WET signed document',
        );
      }
      const formData = new FormData();
      formData.append('file', file);
      return apiUploadWetSignedPDF(uuid, binderPdf.id, formData);
    },
    onSuccess: (signingData: APISigning) => {
      dispatch(setSigning(signingData));
      queryClient.invalidateQueries({ queryKey: ['submission'] });
      onWetSignModalClose();

      toast({
        title: 'WET signed document uploaded successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
        position: 'top',
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Error uploading WET signed document',
        description: error?.error || 'Please try again later.',
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'top',
      });
    },
  });

  const cancelSigningMutation = useMutation({
    mutationFn: () => {
      if (!signing?.id) {
        throw new Error('Signing ID is required for cancellation');
      }
      return apiCancelESigning(uuid, signing.id);
    },
    onSuccess: (signingData: APISigning) => {
      dispatch(setSigning(signingData));
      queryClient.invalidateQueries({ queryKey: ['submission'] });
      onCancelModalClose();

      toast({
        title: 'Signing cancellation initiated',
        status: 'success',
        duration: 3000,
        isClosable: true,
        position: 'top',
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Error canceling signing',
        description: error?.error || 'Please try again later.',
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'top',
      });
    },
  });

  const sendReminderMutation = useMutation({
    mutationFn: () => {
      if (!signing?.id) {
        throw new Error('Signing ID is required to send a reminder');
      }
      return apiSendESigningReminder(uuid, signing.id);
    },
    onSuccess: () => {
      toast({
        title: 'Reminder sent',
        description:
          'A signing reminder is being sent to signers who have not yet signed.',
        status: 'success',
        duration: 3000,
        isClosable: true,
        position: 'top',
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Could not send reminder',
        description: error?.error || 'Please try again later.',
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'top',
      });
    },
  });

  const { data: polledSigningData, isFetching: isRefetching } = useQuery({
    queryKey: ['signing', uuid, signing?.id],
    queryFn: () => apiGetESigning(uuid, signing!.id),
    enabled: !!(
      signing &&
      (signing.status === SIGNING_STATUS_PENDING ||
        signing.status === STATUS_PENDING_CANCELLATION) &&
      signing.id
    ),
    refetchInterval: 5000,
  });

  useEffect(() => {
    if (polledSigningData) {
      dispatch(setSigning(polledSigningData));

      if (polledSigningData.status === SIGNING_STATUS_COMPLETED) {
        toast({
          title: 'Signing completed successfully',
          status: 'success',
          duration: 3000,
          isClosable: true,
          position: 'top',
        });
      } else if (polledSigningData.status === SIGNING_STATUS_ERROR) {
        toast({
          title: 'Signing failed',
          description: 'There was an error with the signing process.',
          status: 'error',
          duration: 5000,
          isClosable: true,
          position: 'top',
        });
      } else if (polledSigningData.status === SIGNING_STATUS_CANCELLED) {
        toast({
          title: 'Signing cancelled',
          description: 'The signing process has been cancelled.',
          status: 'warning',
          duration: 5000,
          isClosable: true,
          position: 'top',
        });
      }
    }
  }, [polledSigningData, dispatch, toast]);

  const handleSendForSigning = () => {
    onConfirmModalClose();
    createSigningMutation.mutate();
  };

  const handleCancelSigning = () => {
    cancelSigningMutation.mutate();
  };

  const handleUploadWetSigned = (file: File) => {
    uploadWetSignedMutation.mutate(file);
  };

  const isBinderComplete =
    binderPdf && binderPdf.status === BINDER_STATUS_COMPLETED;
  const isStepEnabled = isBinderComplete;
  const showSigningButton = !signing;
  const showResetButton =
    signing &&
    (signing.status === SIGNING_STATUS_ERROR ||
      signing.status === SIGNING_STATUS_CANCELLED);

  const getStatusContent = () => {
    if (!isBinderComplete) {
      return (
        <StatusBadge status="disabled">
          No binder has been generated
        </StatusBadge>
      );
    }

    if (!signing) {
      return null;
    }

    if (signing.status === SIGNING_STATUS_PENDING) {
      return (
        <VStack spacing="3" w="100%">
          <StatusBadge status="info">
            <Flex align="center" justify="center" gap="10px">
              {isRefetching ? (
                <>
                  <Spinner size="sm" color="blue.500" />
                  <Text>Checking status...</Text>
                </>
              ) : (
                <Text>Signing in progress...</Text>
              )}
            </Flex>
          </StatusBadge>
          {signing.logs?.some(
            (log) =>
              log.type === 'document_created' || log.type === 'document_sent',
          ) && (
            <>
              {isSigningReminderEnabled && (
                <Button
                  onClick={() => sendReminderMutation.mutate()}
                  disabled={sendReminderMutation.isPending}
                  isLoading={sendReminderMutation.isPending}
                  variant="outline"
                  colorScheme="brand"
                  size="sm"
                  w="100%"
                >
                  Send reminder
                </Button>
              )}
              <Button
                onClick={onCancelModalOpen}
                disabled={cancelSigningMutation.isPending}
                isLoading={cancelSigningMutation.isPending}
                variant="outline"
                colorScheme="red"
                size="sm"
                w="100%"
              >
                Cancel signing
              </Button>
            </>
          )}
        </VStack>
      );
    }

    if (signing.status === STATUS_PENDING_CANCELLATION) {
      return (
        <StatusBadge status="warning">
          <Flex align="center" justify="center" gap="10px">
            {isRefetching ? (
              <>
                <Spinner size="sm" color="orange.500" />
                <Text>Checking status...</Text>
              </>
            ) : (
              <Text>Canceling in progress...</Text>
            )}
          </Flex>
        </StatusBadge>
      );
    }

    if (signing.status === SIGNING_STATUS_ERROR) {
      return (
        <StatusBadge status="error">
          Encountered an issue, please verify the emails are correct and the
          policy effective date/time is in the future and try again
        </StatusBadge>
      );
    }

    if (signing.status === SIGNING_STATUS_CANCELLED) {
      return (
        <StatusBadge status="warning">Signing has been canceled</StatusBadge>
      );
    }

    if (signing.status === SIGNING_STATUS_COMPLETED) {
      return <StatusBadge status="success">Signed by all parties</StatusBadge>;
    }

    return null;
  };

  return (
    <StepContainer isEnabled={!!isStepEnabled}>
      <StepHeader
        stepNumber={2}
        title="Document Signing"
        isEnabled={!!isStepEnabled}
      />

      {binderPdf && signing && signing.logs && signing.logs.length > 0 && (
        <Box mb="15px">
          <Link
            onClick={onOpen}
            color="brand.500"
            textDecoration="underline"
            fontSize="sm"
            fontWeight="semibold"
            cursor="pointer"
          >
            View signing logs
          </Link>
        </Box>
      )}

      {getStatusContent()}

      {isStepEnabled && showSigningButton && (
        <VStack spacing="5" w="100%">
          <Tooltip
            label={
              !applicantEmail || applicantEmail.length === 0
                ? "Please enter applicant's email to enable e-signing"
                : hasEmailConflict()
                  ? 'Each applicant must have a unique email address to confirm their individual identity.\nPlease enter a separate email for each applicant, or select "Wet Signature" to complete the application manually.'
                  : ''
            }
            placement="top"
            hasArrow
            whiteSpace="pre-wrap"
          >
            <Box w="100%">
              <Button
                onClick={onConfirmModalOpen}
                disabled={
                  createSigningMutation.isPending ||
                  !applicantEmail ||
                  applicantEmail.length === 0 ||
                  hasEmailConflict()
                }
                isLoading={createSigningMutation.isPending}
                variant="brand"
                size="sm"
                w="100%"
              >
                Send for E-Signing
              </Button>
            </Box>
          </Tooltip>
          <Button
            onClick={onWetSignModalOpen}
            disabled={uploadWetSignedMutation.isPending}
            isLoading={uploadWetSignedMutation.isPending}
            variant="outline"
            colorScheme="brand"
            size="sm"
            w="100%"
          >
            Upload WET signed Binder
          </Button>
        </VStack>
      )}

      {isStepEnabled && showResetButton && (
        <Button
          onClick={() => resetSigningMutation.mutate()}
          disabled={resetSigningMutation.isPending}
          isLoading={resetSigningMutation.isPending}
          variant="outline"
          colorScheme="red"
          size="sm"
          w="100%"
        >
          Reset signing
        </Button>
      )}

      <SigningLogModal isOpen={isOpen} onClose={onClose} logs={signing?.logs} />

      <ConfirmSigningModal
        isOpen={isConfirmModalOpen}
        onClose={onConfirmModalClose}
        onConfirm={handleSendForSigning}
        signers={signers}
        isLoading={createSigningMutation.isPending}
      />

      <WetSignUploadModal
        isOpen={isWetSignModalOpen}
        onClose={onWetSignModalClose}
        onUpload={handleUploadWetSigned}
        isLoading={uploadWetSignedMutation.isPending}
      />

      <CancelSigningModal
        isOpen={isCancelModalOpen}
        onClose={onCancelModalClose}
        onCancel={handleCancelSigning}
        isLoading={cancelSigningMutation.isPending}
      />
    </StepContainer>
  );
};

export default StepTwo;
